<template>
  <div>
    <TableBillsFilters
      :team-members="teamMembers"
      :recruiters="recruiters"
      :currencies="billsCurrencies"
      @filters-updated="filterUpdated"
    />
    <div class="card card-white">
      <BaseDataTable
        v-if="filterReady"
        :entries="tableData.rows"
        :columns="tableData.columns"
        :loading="$apollo.loading"
        :paginator-info="paginator"
        class="f-12"
        @rows-selected="selectedRows = $event"
        @search="search"
      >
        <template #Bill="{ row }">
          <PopoverBillPublicApi :global-id="row.publicId" :bill-number="row.localId" />
          <WdsLabel v-if="row.isCancelled" error subtle xs>{{ $t('Cancelled') }}</WdsLabel>
          <BaseLabel
            v-else
            v-tooltip="
              row.approvedBy?.name &&
              row.approvedAt &&
              $t('Approved by :name on :date', {
                name: row.approvedBy.name,
                date: moment(row.approvedAt).format('Y-MM-DD'),
              })
            "
            :default="!row.isApproved"
            :success="!!row.isApproved"
            :text="!row.isApproved ? $t('Unapproved') : $t('Approved')"
          />
        </template>

        <template #poNumber="{ row }">
          <span v-if="row.isApproved">{{ row.poNumber }}</span>
          <EditBillPoNumber v-else :bill-id="row.localId" :po-number="row.poNumber" />
        </template>

        <template #Timesheet="{ row }">
          <template v-if="row.hasTimesheet">
            <a
              :data-target="'#timesheetModal' + row.localId"
              :data-test="'timesheet-modal-button-' + row.localId"
              href="#"
              class="f-18"
              data-toggle="modal"
            >
              <span :title="$t('Open Timesheet')" data-toggle="tooltip">
                <i v-if="row.timeSheetFormat === 'sheet'" class="zmdi zmdi-view-list" />
                <i v-else-if="row.timeSheetFormat === 'text'" class="zmdi zmdi-view-headline" />
              </span>
            </a>
            <div
              :id="'timesheetModal' + row.localId"
              :data-test="'timesheet-modal-' + row.localId"
              class="modal fade"
              tabindex="-1"
              role="dialog"
            >
              <div
                :id="`timesheetModal${row.localId}-content`"
                class="modal-dialog"
                role="document"
              >
                <div class="modal-content">
                  <div class="modal-header">
                    <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                      <span aria-hidden="true">&times;</span>
                    </button>
                    <h4 class="modal-title">
                      {{
                        $t('Timesheet for bill #:billno', {
                          billno: row.billNumber,
                        })
                      }}
                    </h4>
                  </div>
                  <div class="modal-body">
                    <div class="tw-text-base tw-flex tw-flex-row tw-gap-1 p-b-20">
                      <strong>{{ $t('Worker') }}:</strong>
                      <WorkerName
                        v-if="row.freelancer"
                        :name="row.freelancer.name"
                        :manages-workers="row.hire?.recruiterManagesWorkers"
                        :recruiter-name="row.recruiter?.name"
                        :recruiter-fee-percentage="row.hire?.recruiterFeeInPercent"
                        :recruiter-ownership-start-date="row.hire?.recruiterOwnershipStartDate"
                        :recruiter-ownership-days="row.hire?.recruiterOwnershipDays"
                        :recruiter-ownership-days-left="row.hire?.recruiterOwnershipDaysLeft"
                        show-label
                      />
                      <em v-else>
                        {{ $t("The freelancer doesn't exist") }}
                      </em>
                    </div>
                    <WdsText v-if="!isJsonString(row.timesheetData)" :text="row.timesheetData" />
                    <TimesheetDisplay
                      v-else
                      :rate-type="JSON.parse(row.timesheetData)?.rateType"
                      :timesheet-lines="JSON.parse(row.timesheetData)?.timesheetLines"
                      :timesheet-rows="JSON.parse(row.timesheetData)?.timesheetRows"
                    />
                  </div>
                </div>
              </div>
            </div>
          </template>
          <template v-if="row.files.data && row.files.data.length > 0">
            <div
              v-for="rowFile of row.files.data"
              :key="hash(rowFile)"
              class="clip-text nowrap p-b-5"
            >
              <a
                :href="`/bills/${row.localId}/files/${rowFile.storageName}`"
                :title="$t('Click to download timesheet document')"
                data-toggle="tooltip"
                target="_blank"
              >
                <i class="zmdi zmdi-file" />
              </a>
            </div>
          </template>
        </template>

        <template #Worker="{ row }">
          <TdFreelancer
            :global-id="row.freelancer?.globalId"
            :profile-url="row.freelancer?.primaryProfile?.url"
            :has-draft-contract="!!row.hasDraftContract"
          >
            <template #name>
              <WorkerName
                :name="row.freelancer?.name || ''"
                :manages-workers="row.hire?.recruiterManagesWorkers"
                :recruiter-name="row.recruiter?.name"
                :recruiter-fee-percentage="row.hire?.recruiterFeeInPercent"
                :recruiter-ownership-start-date="row.hire?.recruiterOwnershipStartDate"
                :recruiter-ownership-days="row.hire?.recruiterOwnershipDays"
                :recruiter-ownership-days-left="row.hire?.recruiterOwnershipDaysLeft"
              />
            </template>
          </TdFreelancer>
        </template>

        <template #HireOwners="{ row }">
          <template v-if="row.hire.owners?.length">
            <GhostComponent
              v-for="(owner, index) in row.hire.owners"
              :key="owner.globalId"
              :global-id="owner.globalId"
              component-name-when-active="ProfileInfoCard"
            >
              <a>
                {{ owner.name }}
                <template v-if="index < row.hire.owners.length - 1"><br /></template>
              </a>
            </GhostComponent>
          </template>
          <GhostComponent
            v-else
            :global-id="row.company.user.globalId"
            component-name-when-active="ProfileInfoCard"
          >
            <a>{{ row.company.user.name }}</a>
          </GhostComponent>
        </template>

        <template #Invoice="{ row }">
          <template v-if="row.invoice">
            <a
              v-if="row.invoice.pdfUrl"
              :href="row.invoice.pdfUrl"
              :title="$t('Download invoice as PDF')"
              target="_blank"
              class="c-black"
            >
              <span class="glyphicon glyphicon-file" aria-hidden="true" />
            </a>

            <a
              v-else
              :title="
                $t('PDF is not available for invoice :invoiceno.', {
                  invoiceno: row.invoice.number || '',
                }) +
                ' ' +
                $t('Contact Worksome to get it re-sent to your email.')
              "
              href=""
              data-toggle="tooltip"
              class="c-gray"
            >
              <span class="glyphicon glyphicon-file" aria-hidden="true" />
            </a>
            <span :value="row.invoice.number ? row.invoice.number : -1" class="c-black f-500">
              {{ row.invoice.number ? '#' + row.invoice.number : '' }}
            </span>
          </template>
          <span
            v-else
            :title="$t('The invoice will be available when the payment request is approved')"
            data-toggle="tooltip"
            class="c-gray faded"
          >
            <span class="glyphicon glyphicon-file quiet" aria-hidden="true" />
          </span>
        </template>

        <template #WorkerPaymentStatus="{ row }">
          <WdsLabel v-if="row.isCancelled" error subtle xs>
            {{ $t('Cancelled') }}
          </WdsLabel>
          <WdsLabel v-else-if="row.paymentStatus === 'paid'" success subtle xs>
            {{ $t('Paid') }}
          </WdsLabel>
          <WdsLabel
            v-else-if="row.paymentStatus === 'prepaid'"
            v-tooltip="
              $t('The freelancer has been paid in advance.') +
              ' ' +
              $t('The final payment on the invoice is still due.')
            "
            success
            subtle
            xs
          >
            {{ $t('Prepaid') }}
          </WdsLabel>
          <WdsLabel
            v-else-if="row.paymentStatus === 'due'"
            v-tooltip="
              row.isOverDue
                ? $t('This payment request is :days days overdue.', { days: row.daysOverDue })
                : $t('This payment request is due on :date.', {
                    date: moment(row.paymentDueDate).format('Y-MM-DD'),
                  })
            "
            warning
            subtle
            xs
          >
            {{ $t('Due') }}
          </WdsLabel>
          <WdsLabel v-else warning subtle xs>
            {{ $t('Unpaid') }}
          </WdsLabel>
        </template>

        <template #Rate="{ row }">
          <div :data-test="`rate-cell-${row.localId}`">
            <WdsLabel
              v-if="row.totalBilledMoneyWithExpenses.cents === row.expenseReportsMoney.cents"
              warning
              subtle
              xs
              class="nowrap"
            >
              {{ $t('Expense only') }}
            </WdsLabel>
            <span v-else>
              {{ row.hasMilestones ? $t('Milestone') : row.rateTypeName }}
              <br />
              {{ row.currencySymbol }} {{ row.rate }}
            </span>
          </div>
        </template>

        <template #Action="{ row }">
          <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
          <modal-decline-bill
            :bill-id="row.localId"
            :bill-updated-at="row.updatedAt"
            :redirect-uri="currentUrl"
          >
            <div :id="`bill-decline-modal-${row.localId}`" class="inline" />
          </modal-decline-bill>
          <ButtonDropdown fixed no-caret>
            <i class="zmdi zmdi-more" />
            <template #dropdown>
              <div class="dropdown open">
                <ul class="dropdown-menu dropdown-menu-right">
                  <template v-if="row.invoice">
                    <template
                      v-if="
                        !row.isCancelled &&
                        !row.invoice.isCompleted &&
                        row.company &&
                        row.company.canPayWithCreditCards
                      "
                    >
                      <li>
                        <a
                          :data-billid="row.localId"
                          :data-bill-updated-at="row.updatedAt"
                          :title="
                            $t('We are expecting a wire transfer with your payment.') +
                            '<br><strong>' +
                            $t('You can also use a credit card to pay now') +
                            '</strong>'
                          "
                          :data-redirecturi="currentUrl"
                          href="#"
                          class="block payment-modalopen"
                          data-toggle="tooltip"
                          data-html="true"
                        >
                          <i class="zmdi zmdi-card" />
                          {{ $t('Pay now') }}
                        </a>
                      </li>
                      <li class="divider" />
                    </template>
                  </template>
                  <template v-else-if="!row.isApproved">
                    <li>
                      <a
                        :data-billid="row.localId"
                        :data-bill-updated-at="row.updatedAt"
                        :data-redirectUri="currentUrl"
                        href="#"
                        class="payment-modalopen"
                      >
                        {{ $t('Approve bill') }}
                      </a>
                    </li>
                    <li>
                      <a
                        href="#"
                        @click="openBillDeclineModal(`bill-decline-modal-${row.localId}`)"
                      >
                        {{ $t('Decline bill') }}
                      </a>
                    </li>
                    <li class="divider" />
                  </template>
                  <li>
                    <a :href="`/messages/${row.threadId}`">
                      {{ $t('Go to conversation') }}
                    </a>
                  </li>
                  <li>
                    <a
                      :href="`/contract/${row.hire ? row.hire.localId : row.localId}/pdf`"
                      target="_blank"
                    >
                      {{ $t('Download contract') }}
                    </a>
                  </li>
                </ul>
              </div>
            </template>
          </ButtonDropdown>
        </template>
      </BaseDataTable>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import gql from 'graphql-tag'
import { markRaw } from 'vue'
import { WdsLabel, WdsText } from '@worksome/wds-components'
import TableBillsFilters from '@/components/TableBillsFilters.vue'
import HashMixin from '@/mixins/MethodHash'
import BaseDataTable from '@/components/BaseDataTable'
import ButtonDropdown from '@/components/ButtonDropdown.vue'
import { TableDataHandler } from '@/vue-prop-types/DataTable'
import { cleanStrict, stringToEnum } from '@/utils/tools'
import BaseHtmlForm from '@/components/BaseHtmlForm.vue'
import { TdDate, TdText, TdFreelancer, TdJob } from '@/components/BaseDataTable/Cells'
import BaseLabel from '@/components/BaseLabel.vue'
import EditBillPoNumber from '@/components/EditBillPoNumber.vue'
import GhostComponent from '@/components/GhostComponent.vue'
import { queryDecodeObject, QueryEncoder } from '@/utils/Encoders'
import WorkerName from '@/components/WorkerName.vue'
import PopoverBillPublicApi from '@/components/PopoverBillPublicApi.vue'
import TimesheetDisplay from '@/components/messages/TimesheetDisplay.vue'
import { $t } from '@/composables/translation'

export default {
  name: 'TableBills',
  components: {
    TimesheetDisplay,
    WdsText,
    PopoverBillPublicApi,
    EditBillPoNumber,
    BaseDataTable,
    GhostComponent,
    BaseLabel,
    TdFreelancer,
    // eslint-disable-next-line vue/no-unused-components -- Baseline eslint ignore please fix if working on this
    TdDate,
    // eslint-disable-next-line vue/no-unused-components -- Baseline eslint ignore please fix if working on this
    BaseHtmlForm,
    TableBillsFilters,
    ButtonDropdown,
    WdsLabel,
    WorkerName,
  },
  mixins: [HashMixin],
  props: {
    billsCurrencies: {
      type: Array,
      default: () => [],
    },
    enableWorksomeTimesheets: {
      type: Boolean,
      default: false,
    },
  },
  apollo: {
    data: {
      skip() {
        return !this.filterReady
      },
      query: gql`
        query Bills(
          $filter: BillsFilterInput
          $order: [BillsOrderByClause!]
          $search: String
          $page: Int!
          $count: Int!
        ) {
          data: me {
            globalId
            localId
            activeAccount {
              globalId
              user {
                globalId
                localId
                name
              }
              __typename
              ... on HasBills {
                bills(
                  filter: $filter
                  orderBy: $order
                  search: $search
                  first: $count
                  page: $page
                ) {
                  paginatorInfo {
                    count
                    currentPage
                    total
                    ...PaginatorInfoFragment
                  }
                  data {
                    globalId
                    localId
                    publicId
                    updatedAt
                    issuedAt
                    approvedAt
                    isApproved
                    approvedBy {
                      globalId
                      name
                    }
                    poNumber
                    billNumber
                    rateTypeName
                    rateType
                    rate
                    days
                    hours
                    weeks
                    currencySymbol
                    isManagedByRecruiter
                    hasRecruiter
                    totalBilledMoneyWithExpenses {
                      cents
                    }
                    expenseReportsMoney {
                      cents
                    }
                    totalPayableMoney {
                      inDec
                      withVat {
                        inDec
                      }
                    }
                    threadId
                    isPayed
                    isCancelled
                    isOverDue
                    daysOverDue
                    paymentDueDate
                    paymentStatus
                    invoice {
                      globalId
                      pdfUrl
                      number
                      isCompleted
                      isBatched
                      deposit {
                        globalId
                        isCompleted
                      }
                    }
                    company {
                      globalId
                      name
                      canPayWithCreditCards
                      user {
                        globalId
                        name
                      }
                    }
                    hasTimesheet
                    worksomeTimesheet {
                      id
                    }
                    timeSheetFormat
                    timeSheetHtml
                    timesheetData
                    freelancer {
                      globalId
                      name
                      primaryProfile {
                        globalId
                        url
                      }
                    }
                    recruiter {
                      globalId
                      name
                    }
                    job: yjob {
                      globalId
                      localId
                      name
                    }
                    payedAt
                    hire {
                      globalId
                      localId
                      recruiterFeeInPercent
                      recruiterManagesWorkers
                      recruiterOwnershipDays
                      recruiterOwnershipDaysLeft
                      recruiterOwnershipStartDate
                      owners {
                        globalId
                        name
                      }
                    }
                    files(first: 100) {
                      data {
                        globalId
                        storageName
                      }
                    }
                    hasMilestones
                  }
                }
              }
              ... on HasRecruiters {
                recruiters(first: 1000) {
                  data {
                    globalId
                    localId
                    name
                  }
                }
              }
              ... on HasTeamMembers {
                teamMembers(
                  first: 1000
                  orderBy: [{ column: NAME, order: ASC }]
                  isHireOwner: true
                ) {
                  data {
                    globalId
                    localId
                    name
                  }
                }
              }
            }
          }
        }
        ${BaseDataTable.fragments.pagination}
      `,
      debounce: 250,
      variables() {
        return {
          filter: { ...this.filter },
          order: this.ordering,
          search: this.searchValue,
          page: this.paginationSettings.page,
          count: this.paginationSettings.displayPerPage,
        }
      },
    },
  },
  data() {
    const odSettings = new QueryEncoder({
      defaultVal: { issuedAt: 'desc' },
      queryPoint: 'ordering',
      decoder: queryDecodeObject,
      filter(data) {
        return cleanStrict({ ...data })
      },
    })
    return {
      filter: {},
      action: 'default',
      paginationSettings: {
        page: 1,
        displayPerPage: 50,
      },
      orderSettings: odSettings,
      searchValue: '',
      columns: [
        {
          title: 'Created',
          key: 'issuedAt',
          slot: 'Date',
          class: 'nowrap',
          renderUsing: {
            component: markRaw(TdDate),
            componentProps: (row) => ({ date: row.issuedAt }),
          },
        },
        {
          title: 'Bill',
          key: 'id',
          sort: 'billNumber',
          class: 'text-left',
          ignoreClickForRowSelection: true,
        },
        {
          title: 'PO no.',
          key: 'poNumber',
          ignoreClickForRowSelection: true,
          slot: 'poNumber',
        },
        {
          title: 'Timesheet',
          key: 'timesheet',
          class: 'skip-font-adjustments',
          orderAble: false,
          ignoreClickForRowSelection: true,
        },
        {
          title: 'Worker',
          slot: 'Worker',
          key: 'freelancer',
          class: 'text-left',
          sort: 'freelancer.name',
        },
        {
          title: 'Hire Owner(s)',
          key: 'hireOwners',
          slot: 'HireOwners',
          orderAble: false,
        },
        {
          title: 'Job',
          class: 'text-left',
          orderAble: false,
          key: 'job',
          renderUsing: {
            component: markRaw(TdJob),
            componentProps: (row) => ({
              localId: row.job?.localId,
              name: row.jobNameOnContract || row.job?.name || '',
              isCompleted: !!row.job?.completed,
              companyName: this.account?.isOrganization ? row.company?.name : '',
            }),
          },
        },
        {
          title: 'Invoice',
          key: 'invoiceId',
          class: 'text-left f-13 nowrap',
          orderAble: false,
          ignoreClickForRowSelection: true,
        },
        {
          title: 'Worker payment status',
          key: 'workerPaymentStatus',
          slot: 'WorkerPaymentStatus',
          class: 'text-left',
          orderAble: false,
          ignoreClickForRowSelection: true,
        },
        {
          title: 'Units',
          key: 'units',
          class: 'f-600 f-13 text-left',
          orderAble: false,
          renderUsing: {
            component: markRaw(TdText),
            componentProps: (row) => ({
              html: (
                (row.weeks !== 0.0 ? row.weeks : null) ??
                (row.days !== 0.0 ? row.days : null) ??
                (row.hours !== 0.0 ? row.hours : null) ??
                0
              ).toString(),
            }),
          },
        },
        {
          title: 'Rate',
          key: 'rate',
          class: 'text-left',
          slot: 'Rate',
        },
        {
          title: 'Total',
          key: 'total',
          sort: 'totalPayableMoney.inDec',
          type: Number,
          class: 'f-500 f-13 text-left tw-whitespace-nowrap',
          renderUsing: {
            component: markRaw(TdText),
            componentProps: (row) => ({
              strikeThrough: row.isCancelled,
              text:
                parseFloat(row.totalPayableMoney.inDec.replace(',', '').replace('.', '')) > 0
                  ? `${row.currencySymbol} ${row.totalPayableMoney.inDec}`
                  : '',
            }),
          },
        },
        {
          title: 'Action',
          key: 'action',
          class: 'text-left',
          orderAble: false,
          ignoreClickForRowSelection: true,
        },
      ].map((h) => {
        // eslint-disable-next-line no-param-reassign -- Baseline eslint ignore please fix if working on this
        h.order = odSettings.data[h.key]
        return h
      }),
      loading: true,
      currentUrl: this.getCurrentUrl(),
      selectedRows: {},
    }
  },
  computed: {
    filterReady() {
      return Object.keys(this.filter).length > 0
    },
    moment: () => moment,
    rows() {
      return this?.data?.activeAccount?.bills?.data || []
    },
    teamMembers() {
      const rawTeamMembers = this?.data?.activeAccount?.teamMembers?.data || []
      if (rawTeamMembers.length) {
        const currentUserId = this?.data?.localId
        const accountOwner = this?.data?.activeAccount?.user
        const teamMembers = []
        // if currentUserId is in the teamMembers list, we want to show it first
        if (rawTeamMembers.find((teamMember) => teamMember.localId === currentUserId)) {
          teamMembers.push({
            value: currentUserId,
            label: this.$t('My own'),
          })
        }
        // If accountOwner is not in the teamMembers list, we want to add it
        if (!rawTeamMembers.find((teamMember) => teamMember.localId === accountOwner?.localId)) {
          teamMembers.push({
            value: accountOwner?.localId,
            label: accountOwner?.name,
          })
        }
        return [
          ...teamMembers,
          ...rawTeamMembers
            .filter(
              (teamMember) => ![currentUserId, accountOwner?.localId].includes(teamMember.localId),
            )
            .map((teamMember) => ({
              value: teamMember.localId,
              label: teamMember.name,
            })),
        ]
      }
      return []
    },
    recruiters() {
      const recruiters = this?.data?.activeAccount?.recruiters?.data
      if (recruiters) {
        return recruiters.map((recruiter) => ({
          value: recruiter.localId,
          label: recruiter.name,
        }))
      }
      return []
    },
    tableData() {
      return new TableDataHandler({
        rows: this.rows,
        columns: this.columns,
      })
    },
    paginator() {
      return this?.data?.activeAccount?.bills?.paginatorInfo
    },
    totalElements() {
      return this.paginator?.total ?? 0
    },
    ordering() {
      const o = []
      for (const key in this.orderSettings.data) {
        if (this.orderSettings.data[key]) {
          o.push({
            field: stringToEnum(key),
            order: stringToEnum(this.orderSettings.data[key]),
          })
        }
      }
      return o
    },
    allSelectedRowsHaveInvoices() {
      const rows = Object.values(this.selectedRows)

      return rows.length > 0 && rows.every((row) => row.invoice?.pdfUrl)
    },
  },
  methods: {
    isJsonString(str) {
      try {
        JSON.parse(str)
      } catch (e) {
        return false
      }
      return true
    },
    $t,
    search(options) {
      this.paginationSettings.displayPerPage = options.perPage
      this.paginationSettings.page = options.page + 1
      this.orderSettings.data = options.order
      this.searchValue = options.search
    },
    filterUpdated($event) {
      this.currentUrl = this.getCurrentUrl()
      // Since the filter might fire at random, we only update our copy when the new filter is different from ours.
      // We also reset out page to one, since the amount of data we have might change such that there are fewer pages
      // for us to paginate.
      if (this.hash(this.filter) !== this.hash($event)) {
        this.filter = $event
        this.paginationSettings.page = 1
      }
    },
    getCurrentUrl() {
      return encodeURIComponent(window.location.pathname + window.location.search)
    },
    openBillDeclineModal(billDeclineModalId) {
      document.getElementById(billDeclineModalId).click()
    },
  },
}
</script>

<style scoped lang="scss">
.label {
  font-size: 9px;
  display: inline-block !important;
  margin: 1px !important;
  padding: 0.3em 0.6em 0.3em !important;
}
</style>
